import type { ReactNode } from 'react';
import { cx } from './cx.js';
import { FormActions } from './FormActions.js';

/**
 * Gabarits d'écran. Ils ne posent que la structure (largeurs, colonnes, espacements) ; le contenu
 * vient des composants. Marges latérales gérées par la mise en page globale.
 */

/**
 * Liste (parc, dossiers, équipe) : en-tête, barre de filtres, puis éléments. Une colonne sur
 * téléphone ; `columns` sur ordinateur pour des cartes côte à côte.
 */
export function ListLayout({
  header,
  toolbar,
  children,
  columns = 1,
  className,
}: {
  header?: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}) {
  return (
    <div className={className}>
      {header}
      {toolbar ? <div className="mb-3">{toolbar}</div> : null}
      <ListGrid columns={columns}>{children}</ListGrid>
    </div>
  );
}

/** Grille d'éléments de liste (cartes). */
export function ListGrid({
  columns = 1,
  children,
  className,
}: {
  columns?: 1 | 2 | 3;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'grid gap-2.5',
        columns === 2 && 'xl:grid-cols-2',
        columns === 3 && 'md:grid-cols-2 xl:grid-cols-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Liste + détail côte à côte (vague 7 : Parc, Dossiers, Messagerie). `split` faux (téléphone,
 * tablette) : la liste seule, le détail s'ouvre en pleine page. `split` vrai : liste à gauche
 * (`--split-list-w`), détail à droite dans un panneau collant qui défile pour lui-même ; sans élément
 * ouvert, `placeholder` explique quoi faire.
 */
export function SplitView({
  split,
  list,
  detail,
  placeholder,
  listLabel,
  detailLabel,
}: {
  split: boolean;
  list: ReactNode;
  detail: ReactNode | null;
  placeholder?: ReactNode;
  listLabel: string;
  detailLabel: string;
}) {
  if (!split) return <>{list}</>;
  return (
    <div className="grid grid-cols-[var(--split-list-w)_minmax(0,1fr)] items-start gap-5">
      <section aria-label={listLabel} className="min-w-0">
        {list}
      </section>
      <section
        aria-label={detailLabel}
        className="sticky top-0 -mt-6 max-h-dvh min-w-0 overflow-y-auto overscroll-contain border-l border-line-soft pt-6 pb-10 pl-5"
      >
        {detail ?? placeholder ?? null}
      </section>
    </div>
  );
}

/**
 * Fiche (dossier, véhicule, client) : bloc d'identité en tête, onglets, contenu. Sur ordinateur,
 * un `aside` facultatif s'affiche dans une colonne de droite.
 */
export function DetailLayout({
  hero,
  tabs,
  aside,
  children,
}: {
  hero: ReactNode;
  tabs?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    // Conteneur nommé (ordinateur) : la fiche suit sa largeur réelle (pleine page ou panneau de détail).
    // Pas sur téléphone : un conteneur capturerait la barre d’actions fixe du mode saisie.
    <div className="lg:@container/fiche">
      {hero}
      {/* Les onglets ne sont pas enveloppés : un onglet « sticky » doit rester enfant direct de la fiche. */}
      {tabs}
      {aside ? (
        <div className="grid gap-5 @4xl/fiche:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">{children}</div>
          <div className="min-w-0">{aside}</div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

/**
 * Formulaire : colonne de lecture (640 px au plus), groupes de champs, barre d'actions (`FormActions` :
 * « Retour » puis l'action principale ; une ligne compacte au-dessus du clavier pendant la saisie).
 */
export function FormLayout({
  header,
  children,
  actions,
  className,
}: {
  header?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('max-w-2xl', className)}>
      {header}
      <div className="space-y-4">{children}</div>
      {actions ? <FormActions>{actions}</FormActions> : null}
    </div>
  );
}

/**
 * Groupe de champs d'un formulaire : titre, description, champs espacés. Quand la carte est assez large
 * (conteneur ≥ 768 px : réglages du Compte sur ordinateur), titre et description passent dans une colonne
 * de gauche et les champs à droite (flottants : la légende reste celle du `fieldset`). Dans une colonne
 * de formulaire étroite (`FormLayout`), tout reste empilé.
 */
export function FormSection({
  title,
  description,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  const head = !!(title || description);
  return (
    <div className="@container rounded-2xl border border-line bg-surface p-(--card-p)">
      <fieldset className="m-0 flow-root min-w-0 border-0 p-0">
        {title ? (
          <legend className="mb-0.5 p-0 text-base font-semibold text-ink @3xl:float-left @3xl:w-[calc(33%-24px)]">{title}</legend>
        ) : null}
        {description ? <p className="text-sm text-ink-3 @3xl:clear-left @3xl:float-left @3xl:w-[calc(33%-24px)]">{description}</p> : null}
        <div className={cx('space-y-3', head && 'mt-3 @3xl:mt-0 @3xl:ml-[33%]')}>{children}</div>
      </fieldset>
    </div>
  );
}

/** Liste de définitions libellé / valeur (fiches). */
export function DataList({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cx('divide-y divide-line-soft', className)}>{children}</dl>;
}

export function DataRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="text-sm text-ink-3">{label}</dt>
      <dd className="text-right text-ink tabular-nums">{children}</dd>
    </div>
  );
}

/**
 * Groupe de réglages (passe « densité ») : une carte, un titre, des lignes `SettingRow` séparées par un
 * filet léger, et une barre d'actions facultative alignée à droite. Pensé pour Compte : sur ordinateur,
 * chaque ligne passe en deux colonnes (libellé et description à gauche, contrôle à droite).
 */
export function SettingsGroup({
  title,
  description,
  children,
  footer,
  className,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Actions (Enregistrer, Annuler), alignées à droite sous les lignes. */
  footer?: ReactNode;
  className?: string;
  id?: string;
}) {
  const headingId = id ? `${id}-titre` : undefined;
  return (
    <section id={id} aria-labelledby={title ? headingId : undefined} className={cx('rounded-2xl border border-line bg-surface', className)}>
      {title || description ? (
        <header className="border-b border-line-soft px-(--card-p) py-3">
          {title ? (
            <h2 id={headingId} className="text-base font-semibold text-ink">
              {title}
            </h2>
          ) : null}
          {description ? <p className="mt-0.5 text-sm text-ink-3">{description}</p> : null}
        </header>
      ) : null}
      <div className="divide-y divide-line-soft px-(--card-p)">{children}</div>
      {footer ? (
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line-soft px-(--card-p) py-2.5 [&>*]:flex-1 sm:[&>*]:flex-none">
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

/**
 * Ligne de réglage : libellé (et description) à gauche, contrôle à droite sur ordinateur (≥ 1 024 px) ;
 * empilés sur téléphone. `htmlFor` relie le libellé au champ.
 */
export function SettingRow({
  label,
  description,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode;
  description?: ReactNode;
  htmlFor?: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx('gap-x-8 gap-y-2 py-3 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-center', className)}>
      <div className="min-w-0">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="block text-base font-medium text-ink">
            {label}
          </label>
        ) : (
          <p className="text-base font-medium text-ink">{label}</p>
        )}
        {description ? <p className="mt-0.5 text-sm text-ink-3">{description}</p> : null}
      </div>
      {children ? <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2 lg:mt-0 lg:justify-end">{children}</div> : null}
    </div>
  );
}
